// My Global Pub (Family) -> "Advertising Content" of the selected category:
//   - Existing images: the historical pub images of the category (unchanged)
//   - Elakay Swiper: a Swiper Manager swiper, by reference, drawn instead of
//     the image slider (slides, animations, links, responsive settings kept)
// Stored as the category page's "banner" placement (SwiperPlacement: category
// id, swiper id, slot, status, created by, updated at) -- saved with the same
// atomic, revision-guarded page layout write as Animation Settings, so the
// category's other swipers are never touched. The images are never deleted:
// switching back to "Existing images" is one click.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaExchangeAlt, FaImages, FaSave, FaSpinner, FaTrash } from "react-icons/fa";
import { elakayAutoText } from "../pageInside/dataElakay";
import { getSwiperPageLayout, saveSwiperPageLayout } from "../auth/apiDynamicSwipers";
import { SLOT_BANNER } from "./dynamicSwiperRegistry";
import SwiperLibraryPicker from "./SwiperLibraryPicker";
import { DevicePreview, Segmented, StatusBadge, SwiperThumb, errorMessage } from "./SwiperAdminUI";
import { useElakaySwiper } from "./ElakaySwiperBlock";
import "./SwiperAdmin.css";

const text = elakayAutoText;
const MODE_GALLERY = "gallery";
const MODE_SWIPER = "swiper";

const CategoryBannerSwiperPanel = ({ categoryId, categoryName = "" }) => {
  const page = useMemo(() => (categoryId ? { pageType: "category", categoryId: String(categoryId) } : null), [categoryId]);
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("gallery");
  const [chosen, setChosen] = useState(null); // { id, name } picked, not saved yet
  const [picking, setPicking] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const flash = (type, message) => setNotice({ type, message });

  const saved = useMemo(() => (layout?.placements || []).find((placement) => placement.slotKey === SLOT_BANNER) || null, [layout]);

  const load = useCallback(async () => {
    if (!page) return;
    setLoading(true);
    try {
      const next = await getSwiperPageLayout(page);
      const banner = (next.placements || []).find((placement) => placement.slotKey === SLOT_BANNER);
      setLayout(next);
      setMode(banner ? MODE_SWIPER : MODE_GALLERY);
      setChosen(banner ? { id: banner.swiperId, name: banner.swiper?.name || "" } : null);
      setError(null);
    } catch (loadError) {
      setError(loadError);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    setNotice(null);
    setPreviewing(false);
    load();
  }, [load]);

  // A swiper just picked comes with its full data from the library; the
  // saved banner is read like on the live page.
  const { swiper: liveSwiper } = useElakaySwiper(mode === "swiper" && !chosen?.swiper ? chosen?.id : null);
  const chosenSwiper = chosen?.swiper || liveSwiper;
  const dirty = mode === "gallery" ? Boolean(saved) : Boolean(chosen && chosen.id !== saved?.swiperId);

  const save = async () => {
    if (!layout || !page) return;
    setSaving(true);
    setNotice(null);
    // Every other swiper of this category page is kept exactly as it is.
    const others = (layout.placements || [])
      .filter((placement) => placement.slotKey !== SLOT_BANNER && placement.swiperId !== chosen?.id)
      .map((placement) => ({ swiperId: placement.swiperId, slotKey: placement.slotKey, active: placement.active }));
    const placements = mode === "swiper" && chosen ? [{ swiperId: chosen.id, slotKey: SLOT_BANNER, active: true }, ...others] : others;
    try {
      const next = await saveSwiperPageLayout(page, placements, layout.revision);
      setLayout(next);
      flash("success", mode === "swiper"
          ? text("h_gpub_saved_swiper", "\"{name}\" is now the advertising banner of {category}.", { name: chosen?.name || "", category: categoryName })
          : text("h_gpub_saved_gallery", "{category} shows its advertising images again.", { category: categoryName }),
      );
    } catch (saveError) {
      if (saveError.code === "LAYOUT_CONFLICT") {
        await load();
        flash("warning", text("h_dsw_layout_conflict", "These swipers were changed elsewhere in the meantime. The latest version was reloaded — apply your changes again."));
      } else {
        flash("error", errorMessage(saveError, text("h_gpub_save_error", "The advertising banner could not be saved.")));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!page) return null;

  return (
    <div className="dswa pub-section-card gpub-banner" data-testid="global-pub-swiper">
      <div className="dswa-compose-head">
        <div>
          <h3 className="dswa-panel-title"><span><FaImages aria-hidden="true" /> {text("h_gpub_title", "Advertising Content")}</span></h3>
          <p className="dswa-hint" style={{ margin: "4px 0 0" }}>
            {text("h_gpub_hint", "Choose what {category} shows at the top of its page: its advertising images, or an Elakay Swiper from the Swiper Manager (with all its slides, animations and links).", { category: categoryName || "—" })}
          </p>
        </div>
        <button type="button" className="dswa-btn dswa-btn-primary" onClick={save} disabled={!dirty || saving || loading || (mode === "swiper" && !chosen)}>
          {saving ? <FaSpinner className="dswa-spin" aria-hidden="true" /> : <FaSave aria-hidden="true" />}
          {text("h_gpub_save", "Save")}
        </button>
      </div>
      {error && <div className="dswa-notice dswa-notice-error" role="alert"><span>{errorMessage(error, text("h_gpub_load_error", "The advertising banner could not be loaded."))}</span></div>}
      {notice && <div className={`dswa-notice dswa-notice-${notice.type}`} role={notice.type === "error" ? "alert" : "status"}><span>{notice.message}</span></div>}

      <Segmented
        value={mode}
        onChange={(value) => { setMode(value); setNotice(null); }}
        label={text("h_gpub_title", "Advertising Content")}
        options={[
          { value: "gallery", label: text("h_gpub_mode_gallery", "Existing images / gallery") },
          { value: "swiper", label: text("h_gpub_mode_swiper", "Elakay Swiper") },
        ]}
      />

      {mode === "gallery" && (
        <p className="dswa-hint" style={{ margin: 0 }}>
          {saved
            ? text("h_gpub_gallery_pending", "Save to remove the swiper \"{name}\" and show the images again (the swiper itself is kept in the Swiper Manager).", { name: saved.swiper?.name || "" })
            : text("h_gpub_gallery_active", "The images managed below are shown.")}
        </p>
      )}

      {mode === "swiper" && (
        <div className="gpub-swiper">
          {chosen ? (
            <div className="gpub-swiper-card">
              <SwiperThumb url={chosenSwiper?.slides?.[0]?.imageUrl} aspectRatio={chosenSwiper?.aspectRatio} slide={chosenSwiper?.slides?.[0]} />
              <div className="gpub-swiper-meta">
                <strong>{chosen.name || chosenSwiper?.name || "—"}</strong>
                {chosen.id === saved?.swiperId
                  ? <span className="dswa-badge dswa-badge-active">{text("h_gpub_current", "Current banner")}</span>
                  : <span className="dswa-badge dswa-badge-warning">{text("h_gpub_not_saved", "Not saved yet")}</span>}
                {saved?.swiper && chosen.id === saved.swiperId && <StatusBadge status={saved.swiper.status} />}
                {!chosenSwiper && <span className="dswa-hint">{text("h_gpub_inactive", "This swiper is inactive: the images are shown until it is active again.")}</span>}
              </div>
              <div className="dswa-actions">
                <button type="button" className="dswa-btn dswa-btn-sm" onClick={() => setPreviewing((open) => !open)} aria-pressed={previewing ? "true" : "false"} disabled={!chosenSwiper}>
                  {text("h_dsw_preview", "Preview")}
                </button>
                <button type="button" className="dswa-btn dswa-btn-sm" onClick={() => setPicking(true)}>
                  <FaExchangeAlt aria-hidden="true" /> {text("h_gpub_replace", "Replace")}
                </button>
                <button type="button" className="dswa-btn dswa-btn-sm dswa-btn-danger" onClick={() => { setChosen(null); setMode(MODE_GALLERY); }}>
                  <FaTrash aria-hidden="true" /> {text("h_gpub_remove", "Remove")}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="dswa-add-here" onClick={() => setPicking(true)}>
              {text("h_gpub_select", "Select an Elakay Swiper")}
            </button>
          )}
          {previewing && chosenSwiper && (
            <DevicePreview swiper={chosenSwiper} device={device} onDeviceChange={setDevice} />
          )}
        </div>
      )}

      <SwiperLibraryPicker
        show={picking}
        selectedId={chosen?.id || null}
        title={text("h_gpub_picker_title", "Select an Elakay Swiper for {category}", { category: categoryName || "—" })}
        onClose={() => setPicking(false)}
        onPick={(swiper) => {
          setPicking(false);
          setChosen({ id: swiper.id, name: swiper.name, swiper });
          setMode(MODE_SWIPER);
          setNotice(null);
        }}
      />
    </div>
  );
};

export default CategoryBannerSwiperPanel;
